import {
  Children,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useId,
} from 'react';
import { Label } from '@/components/ui/label';
import {
  SelectContent,
  SelectItem,
  Select as SelectRoot,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select-base';
import { cn } from '@/lib/utils';

interface OptionProps {
  value?: string;
  disabled?: boolean;
  children?: ReactNode;
}

/** The select's value cannot be the empty string, which the portal uses for "Any". */
const EMPTY = '__empty__';
function toKey(value: string | undefined): string | undefined {
  if (value === undefined) return undefined;
  return value === '' ? EMPTY : value;
}
function fromKey(key: unknown): string {
  if (typeof key !== 'string' && typeof key !== 'number') return '';
  return key === EMPTY ? '' : String(key);
}

interface SelectProps {
  /** A visible label above the control, which is also its accessible name. */
  label?: string;
  'aria-label'?: string;
  id?: string;
  name?: string;
  value?: string;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  /** Called with `{ target: { value } }`, so handlers written for a native select keep working. */
  onChange?: (event: { target: { value: string } }) => void;
  /** `<option value="x">Text</option>` elements, as for a native select. */
  children: ReactNode;
}

function optionText(node: ReactNode): string {
  return Children.toArray(node)
    .map((child) =>
      typeof child === 'string' || typeof child === 'number'
        ? String(child)
        : '',
    )
    .join('');
}

/**
 * The shadcn Select behind a native select's props: give it `<option>`
 * children and an `onChange` that reads `event.target.value`.
 */
export function Select({
  label,
  value,
  disabled,
  className,
  placeholder,
  onChange,
  children,
  id,
  name,
  'aria-label': ariaLabel,
}: Readonly<SelectProps>) {
  const autoId = useId();
  const triggerId = id ?? autoId;
  const options = Children.toArray(children)
    .filter((child): child is ReactElement<OptionProps> =>
      isValidElement(child),
    )
    .map((option) => {
      const text = optionText(option.props.children);
      return {
        key: toKey(String(option.props.value ?? text)) ?? text,
        text,
        disabled: option.props.disabled,
      };
    });

  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      {label ? <Label htmlFor={triggerId}>{label}</Label> : null}
      <SelectRoot
        name={name}
        disabled={disabled}
        value={toKey(value) ?? null}
        items={options.map((option) => ({
          value: option.key,
          label: option.text,
        }))}
        onValueChange={(next) =>
          onChange?.({ target: { value: fromKey(next) } })
        }
      >
        <SelectTrigger
          id={triggerId}
          aria-label={label ? undefined : ariaLabel}
          className="w-full"
        >
          <SelectValue placeholder={placeholder ?? 'Select'} />
        </SelectTrigger>
        <SelectContent alignItemWithTrigger={false}>
          {options.map((option) => (
            <SelectItem
              key={option.key}
              value={option.key}
              disabled={option.disabled}
            >
              {option.text}
            </SelectItem>
          ))}
        </SelectContent>
      </SelectRoot>
    </div>
  );
}
